/* eslint-disable @typescript-eslint/no-floating-promises */
import type { SxProps } from '@mui/system';
import type { GetServerSideProps } from 'next';
import { useTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import type { PropsWithChildren } from 'react';
import React, { useEffect, useMemo, useState } from 'react';
import { useWindowSize } from 'react-use';
import { validate as uuidValidate } from 'uuid';

import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';

// Hooks
import useAppRouter from 'src/hooks/useAppRouter';
import useModalOpenState from 'src/hooks/useModalOpenState';
import useUser from 'src/hooks/useUser';
import formatDateToIso from 'src/utils/formatDateToIso';

// Components
import AdGallery from 'src/components/ad/AdGallery';
import AdHeader from 'src/components/ad/AdHeader';
import Assets from 'src/components/ad/Assets';
import Characteristics from 'src/components/ad/Characteristics';
import Consumption from 'src/components/ad/ConsumptionAndEmission';
import ContactBlock from 'src/components/ad/ContactBlock';
import { PropertyEvaluationSection } from 'src/components/ad/PropertyEvaluationSection';
import { ImageContainer } from 'src/components/edit-profile/MyPropertyCard';
import FullWidthLayout from 'src/components/other/FullWidthLayout';
import Button from 'src/components/ui/Button';
import ExpandableText from 'src/components/ui/ExpandableText';
import GoogleMapWithMarker from 'src/components/map/GoogleMapWithMarker';
import Icon from 'src/components/ui/Icon';
import Lightbox from 'src/components/ui/Lightbox';
import Loader from 'src/components/ui/Loader';
import LoanSimulator from 'src/components/ui/LoanSimulator';
import PriceEvaluationTag from 'src/components/ui/PriceEvaluationTag';
import Tag from 'src/components/ui/Tag';
import Typography from 'src/components/ui/Typography';
import PageNotFound from 'src/pages/404';
// Data
import { useAdQuery, useIncrementStatsViewCountMutation } from 'src/hooks/__generated__/queries';
import type { AdQuery, Medium } from 'src/types/__generated__/graphql';
import { Ad, AdStatuses, SaleFeeType } from 'src/types/__generated__/graphql';

// Constants
import { breakpoints } from 'src/constants/breakpoints';
import { colours } from 'src/constants/colours';

// Utils
import AdDetail from 'src/components/ad/AdDetail';
import { EventParamKeys } from 'src/utils/analytics';
import { getAuthCookie } from 'src/utils/authCookie';
import { disableErrorIfJest } from 'src/utils/disableErrorIfJest';
import formatPrice from 'src/utils/formatPrice';
import getApolloClient from 'src/utils/getApolloClient';
import { getUnorderedListColumns } from 'src/utils/getUnorderedListColumns';
import isNotNull from 'src/utils/isNotNull';
import { removeAllHTMLTags, sanitizeHTMLContent } from 'src/utils/sanitizeHtml';
import { styledComponent, styledTag } from 'src/utils/styled';
import AdStats from 'src/components/ad/AdStats';
import HomeAttributeIconBar, { HomeAttributeIconBarVariants } from 'src/components/ad/HomeAttributeIconBar';
import { AnalyticsContextProvider } from 'src/hooks/useDynamicLogEvents';
import isNumber from 'src/utils/isNumber';
import { PartnershipsBlock } from 'src/components/ad/PartnershipBlock';

const StyledContainer = styledComponent(Container)`
    margin-bottom: 3rem;

    .gallery {
        @media (max-width: ${breakpoints.tabletPortrait - 1}px) {
            margin: 0 -1rem;
        }
    }
`;

const StickyArea = styledComponent(Grid)`
    position: relative;
`;

const StickyContainer = styledTag('div')`
    display: flex;
    flex-direction: column;
    position: sticky;
    top: 1rem;
    gap: 1rem;
`;

const TagContainer = styledComponent(Box)`
    gap: 0.5rem;
`;

const StyledBox = styledComponent(Box)`
    > *:last-child {
        margin-left: auto;
    }
`;

const galleryButtonStyle: SxProps = {
    position: 'absolute',
    bottom: '1rem',
    right: '1rem',
};

const AnchorLink = styledTag('a')`
    text-decoration: none;
    &:hover {
        text-decoration: underline;
    }
`;

const ExternalLink = styledTag('a')`
    color: ${colours.secondary.main};
    &:hover {
        text-decoration: underline;
    }
    text-decoration: none;
`;

const MapGrid = styledComponent(Grid)`
    height: 30rem;
    margin: 1rem 0;
    border-radius: 1rem;
    overflow: hidden;`;

const StyledHeader = styledComponent(Grid)`
    display: flex;
    flex-direction: column;

    @media (min-width: ${breakpoints.tabletPortrait}px) {
        display: flex;
        flex-direction: row;
        justify-content: space-between;
    }
`;

const StyledSectionHeader = styledTag('h2')`
    font-size: 1.25rem;
    font-weight: 700;
`;

const StyledPriceDetailsBox = styledComponent(Box)`
font-size: 0.875rem;
& > :first-child {
    margin-top: 0.75rem;
}
`;

interface StyledUnorderedListProps {
    $cols?: number;
    $half?: boolean;
}
const StyledUnorderedList = styledTag('ul')<StyledUnorderedListProps>`
    padding-left: 0rem;
    display: grid;
    grid-template-columns: repeat(${(props) => props.$cols ?? 3}, auto);
    grid-column-gap: 1rem;
    grid-row-gap: 0.75rem;
    ${({ $half }) => ($half ? 'max-width: 50%;' : '')}
`;

const StyledTypography = styledComponent(Typography)<{ component?: string }>``;

const SaleFeeText = styledComponent(Typography)`
    font-size: 0.75rem;
    line-height: 1.2;
    text-align: left;

    @media (min-width: ${breakpoints.tabletPortrait}px) {
        text-align: right;
    }
`;

SaleFeeText.defaultProps = {
    variant: 'subtitle2',
};

const AdPage: PageComponent = () => {
    const { t, i18n } = useTranslation(['ad', 'common']);
    const router = useAppRouter();
    const user = useUser();
    const { width } = useWindowSize();
    const [incrementStatsViewCount] = useIncrementStatsViewCountMutation();

    const { props: lightboxProps, open: openLightBoxModal } = useModalOpenState();
    const [lightboxMainImageUrl, setLightboxMainImageUrl] = useState<string | undefined>(undefined);

    const { isMobile, isTabletPortait, isDesktop } = useMemo(
        () => ({
            isMobile: width < breakpoints.tabletPortrait,
            isTabletPortait: width >= breakpoints.tabletPortrait && width < breakpoints.tabletLandscape,
            isDesktop: width >= breakpoints.desktopSmall,
        }),
        [width],
    );

    const adId = router.query.adId as string;
    const { data, loading } = useAdQuery({
        variables: {
            id: adId as string,
        },
        skip: !adId || !uuidValidate(adId),
    });

    useEffect(() => {
        const handleIncrementStatsViewCountMutation = async (id: string) => {
            await incrementStatsViewCount({
                variables: {
                    adId: id,
                },
                refetchQueries: ['Ad', 'Ads'],
                awaitRefetchQueries: true,
            }).catch((err) => {
                disableErrorIfJest(err);
            });
        };
        if (data?.ad?.id) {
            handleIncrementStatsViewCountMutation(data.ad.id);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data?.ad?.id]);

    const analyticsValue = useMemo(
        () => ({
            [EventParamKeys.ItemId]: adId,
            [EventParamKeys.ItemName]: data?.ad?.title ?? undefined,
            [EventParamKeys.PropertyType]: data?.ad?.propertyType,
        }),
        [adId, data?.ad?.propertyType, data?.ad?.title],
    );

    const isRentChargesIncluded = data?.ad?.isRentChargesIncluded ?? false;
    if (loading) {
        return <Loader />;
    }

    if (data && !data.ad) {
        return <PageNotFound />;
    }

    const unorderedListCol = getUnorderedListColumns(isMobile, isTabletPortait);

    const monthlyCharges = data?.ad?.monthlyCharges;
    const rent = data?.ad?.rent;

    const priceString =
        data?.ad?.type === 'sell'
            ? formatPrice({ price: Number(data.ad.price), lang: i18n.language })
            : t('ad:rentPrice', {
                  rentPrice: formatPrice({
                      price: Number(rent),
                      lang: i18n.language,
                  }),
              });
    const sqMeterNumerator = data?.ad?.price ?? rent;
    const sqMeterPrice =
        sqMeterNumerator && data?.ad?.livingArea
            ? `${formatPrice({
                  price: sqMeterNumerator / data.ad.livingArea,
                  lang: i18n.language,
              })}/m²`
            : null;

    const loanPrice = formatPrice({
        price: Number(data?.ad?.standardLoanAmount?.monthlyPayment),
        lang: i18n.language,
    });

    const showInterOfficeTag = user?.isRealEstatesTrades && data?.ad?.interOffice;

    // All ad types have "rooms" fields except 'parking' and 'land' ads.
    // We can tell if this ad has "rooms" from it's property type.
    const hasRooms = !['parking', 'land'].includes(String(data?.ad?.propertyType));

    const rentDetails =
        data?.ad?.type === 'rent'
            ? [
                  {
                      condition: true,
                      leftText: t('ad:rentPriceAbout'),
                      rightText: priceString,
                      dataTestId: 'rent-price',
                      after: (
                          <StyledTypography
                              variant="subtitle2"
                              data-target-id="rent-price-charges"
                              component="span"
                              color={colours.primary.O72}
                          >
                              {isRentChargesIncluded
                                  ? t('ad:includingChargesInbracket')
                                  : t('ad:excludingChargesInbracket')}
                          </StyledTypography>
                      ),
                  },
                  {
                      condition: isNotNull(monthlyCharges),
                      leftText: t('ad:monthlyCharges'),
                      rightText: t('ad:monthlyChargesPrice', {
                          monthlyCharges,
                      }),
                      dataTestId: 'monthly-charges',
                  },
                  {
                      leftText: t('ad:fees'),
                      rightText: data.ad.fees
                          ? formatPrice({ price: Number(data.ad.fees), lang: i18n.language })
                          : t('ad:yesNoNa.na'),
                      dataTestId: 'ad-fees',
                  },
                  {
                      condition: isNotNull(data.ad.inventory),
                      leftText: t('ad:inventory'),
                      rightText: formatPrice({
                          price: Number(data.ad.inventory),
                          lang: i18n.language,
                      }),
                      dataTestId: 'ad-inventory',
                      leftProps: { color: colours.primary.O72 },
                  },
                  {
                      condition: isNotNull(data.ad.guarantees) && isNumber(data.ad.guarantees),
                      leftText: t('ad:requiredGuarantees'),
                      rightText: formatPrice({
                          price: Number(data.ad.guarantees),
                          lang: i18n.language,
                      }),
                      dataTestId: 'ad-guarantees',
                  },
              ]
            : [];

    const additionalPriceDetails = data?.ad && (
        <StyledPriceDetailsBox display="flex" flexDirection="column" alignItems={isMobile ? undefined : 'flex-end'}>
            {data.ad.type === 'rent' && (
                <Typography
                    align={isMobile ? undefined : 'right'}
                    data-target-id="charges-info"
                    sx={{
                        fontSize: '0.875rem',
                        color: colours.primary.main,
                    }}
                >
                    {isRentChargesIncluded ? t('ad:includingCharges') : t('ad:excludingCharges')}
                </Typography>
            )}
            {data.ad.owner?.user?.isProfessionnal &&
                data.ad.type === 'sell' &&
                (data.ad.saleFeeType === SaleFeeType.Buyer ? (
                    <Typography
                        sx={{
                            fontSize: '0.875rem',
                            color: colours.primary.main,
                        }}
                        align={isMobile ? undefined : 'right'}
                        data-target-id="sale-fee-buyer"
                    >
                        {t('ad:saleFeeTypes.buyer')}
                        <br />
                        {t('ad:saleFeeBuyerRate', {
                            saleFeeBuyerRate: new Intl.NumberFormat(i18n.language).format(
                                Number(data.ad.saleFeeBuyerRate),
                            ),
                        })}
                        <br />
                        {t('ad:priceWithoutSaleFee', {
                            priceWithoutSaleFee: formatPrice({
                                price: data.ad.priceWithoutSaleFee ?? 0,
                                lang: i18n.language,
                            }),
                        })}
                    </Typography>
                ) : (
                    <Typography
                        sx={{
                            fontSize: '0.875rem',
                            color: colours.primary.main,
                        }}
                        align={isMobile ? undefined : 'right'}
                        data-target-id="sale-fee-seller"
                    >
                        {t('ad:saleFeeTypes.seller')}
                    </Typography>
                ))}

            {data.ad.commissionRate && user?.isAbleToJoinOrCreateEstateCompany ? (
                <Typography
                    sx={{
                        fontSize: '0.875rem',
                        color: colours.primary.main,
                    }}
                    align={isMobile ? undefined : 'right'}
                    data-target-id="commission-rate"
                >
                    {t('ad:commissionRate', {
                        commissionRate: data.ad.commissionRate,
                    })}
                </Typography>
            ) : (
                data.ad.type === 'sell' && (
                    <StyledTypography
                        component="span"
                        color="secondary"
                        align={isMobile ? undefined : 'right'}
                        data-target-id="loan-price"
                        mt="0.75rem"
                        sx={{
                            fontSize: '0.875rem',
                        }}
                    >
                        <AnchorLink href="#simulator">
                            {t('ad:loanPrice', {
                                loanPrice,
                            })}
                        </AnchorLink>
                    </StyledTypography>
                )
            )}
            {data.ad.type === 'rent' && data.ad.availableDate && (
                <Box display="flex">
                    <Typography
                        align={isMobile ? undefined : 'right'}
                        sx={{ fontSize: '0.875rem' }}
                        data-target-id="available-date"
                    >
                        {t('ad:availableDate', {
                            date: formatDateToIso(
                                new Date(data.ad.availableDate * 1000).toString(),
                                i18n.language as SupportedLangs,
                            ),
                        })}
                    </Typography>
                </Box>
            )}
        </StyledPriceDetailsBox>
    );

    if (!data?.ad) {
        return null;
    }

    return (
        <AnalyticsContextProvider value={analyticsValue}>
            <StyledContainer>
                <AdHeader ad={data.ad} />
                <ImageContainer $status={data.ad.status}>
                    <div className="gallery">
                        <AdGallery
                            open={(url) => {
                                setLightboxMainImageUrl(url);
                                openLightBoxModal();
                            }}
                            images={data.ad.media as Medium[]}
                        >
                            {Boolean(data.ad.media.length) && (
                                <Button
                                    sx={galleryButtonStyle}
                                    onClick={openLightBoxModal}
                                    startIcon={<Icon name="PictureWhite" />}
                                >
                                    {data.ad.media.length}
                                </Button>
                            )}
                        </AdGallery>
                        {lightboxProps.open && (
                            <Lightbox
                                {...lightboxProps}
                                images={data.ad.media as Medium[]}
                                mainImageUrl={lightboxMainImageUrl}
                                enableZoom
                            />
                        )}
                    </div>
                </ImageContainer>

                <Grid spacing={5} style={{ marginTop: '0.25rem' }} container>
                    <Grid xs={12} sm={12} md={12} lg={8} xl={8} item>
                        <AdStats ad={data.ad} isMobile={isMobile} />
                        <TagContainer display="flex" mb={1}>
                            {data.ad.isNew && data.ad.status !== AdStatuses.Ended && (
                                <Tag size="midsmall" color="tertiary" label={t('ad:new')} />
                            )}
                            {showInterOfficeTag && data.ad.status !== AdStatuses.Ended && (
                                <Tag size="midsmall" color="secondary" label={t('ad:interOffice')} />
                            )}
                            {data.ad.status === AdStatuses.Ended && (
                                <Tag
                                    size="midsmall"
                                    color="tertiary"
                                    label={t(`edit-profile:${data.ad.type === 'sell' ? 'soldLabel' : 'rentedLabel'}`)}
                                />
                            )}
                        </TagContainer>
                        <StyledHeader container>
                            <Grid xs={12} sm={8} md={8} lg={8} xl={8} item>
                                <Typography
                                    variant="h1"
                                    data-target="ad-title"
                                    data-target-id="ad-title"
                                    sx={{
                                        lineHeight: '1.2',
                                    }}
                                >
                                    {data.ad.title}
                                </Typography>

                                <Box mt={1} display="flex">
                                    <Typography
                                        data-target="ad-address"
                                        data-target-id="ad-address"
                                        variant="body1"
                                        sx={{ fontSize: '0.875rem' }}
                                    >
                                        {data.ad.isHiddingCompleteAddress || !data.ad.address.description.length
                                            ? `${data.ad.address.cityName}, ${data.ad.address.zipCode}`
                                            : data.ad.address.description}
                                    </Typography>
                                </Box>

                                <Box display="flex" alignItems="baseline" flexWrap="wrap" gap="0.5rem">
                                    <HomeAttributeIconBar
                                        ad={data.ad as Ad}
                                        homePropertiesDensity={HomeAttributeIconBarVariants.LARGE}
                                    />
                                </Box>
                            </Grid>
                            <Grid xs={12} sm={4} md={4} lg={4} xl={4} mt={isMobile ? '1.5rem' : undefined} item>
                                <Box
                                    display="flex"
                                    flexDirection={isMobile ? 'row' : 'column'}
                                    alignItems={isMobile ? 'center' : 'flex-end'}
                                    justifyContent={isMobile ? 'space-between' : undefined}
                                    width={isMobile ? '100%' : undefined}
                                    gap="0.5rem"
                                >
                                    <Typography
                                        data-target="ad-price"
                                        data-target-id="ad-price"
                                        sx={{
                                            fontSize: '2rem',
                                            fontWeight: '700',
                                            lineHeight: '1.2',
                                            color: colours.primary.main,
                                        }}
                                    >
                                        {priceString}
                                    </Typography>
                                    <Box
                                        display="flex"
                                        sx={{ gap: '0.5rem' }}
                                        flexDirection={isMobile ? 'column' : 'row'}
                                        alignItems={isMobile ? 'flex-end' : 'center'}
                                    >
                                        <Typography
                                            data-target-id="sq-meter-price"
                                            sx={{
                                                fontSize: '0.875rem',
                                                color: colours.primary.main,
                                            }}
                                        >
                                            {sqMeterPrice}
                                        </Typography>
                                        {data.ad.type === 'sell' && data.ad.price && data.ad.homadataEvaluation && (
                                            <PriceEvaluationTag
                                                price={data.ad.price}
                                                confidenceMin={data.ad.homadataEvaluation.confidenceMin}
                                                confidenceMax={data.ad.homadataEvaluation.confidenceMax}
                                                size="midsmall"
                                            />
                                        )}
                                    </Box>
                                </Box>
                                {additionalPriceDetails}
                            </Grid>
                        </StyledHeader>

                        {!isDesktop && (
                            <Section>
                                <ContactBlock ad={data.ad} />
                            </Section>
                        )}

                        <Section>
                            <StyledSectionHeader>{t('ad:description')}</StyledSectionHeader>
                            <Typography
                                data-target-id="ad-description"
                                dangerouslySetInnerHTML={{
                                    __html: sanitizeHTMLContent(data.ad.description),
                                }}
                                sx={{
                                    whiteSpace: 'pre-line',
                                    overflowWrap: 'anywhere',
                                }}
                            />
                        </Section>

                        {data.ad.type === 'rent' && (
                            <Section>
                                <StyledSectionHeader>{t('ad:aboutPrice')}</StyledSectionHeader>
                                <StyledUnorderedList $cols={1} data-test-id="ad-rent-section" $half>
                                    {rentDetails.map((detail) => (
                                        <AdDetail key={detail.dataTestId} {...detail} />
                                    ))}
                                </StyledUnorderedList>

                                {data.ad.inclusions && (
                                    <StyledBox my={1} display="flex" flexDirection="row">
                                        <Typography>{t('ad:inclusions')}</Typography>
                                        <Box width="35rem">
                                            <ExpandableText
                                                dataTargetId="ad-inclusions"
                                                lines={1}
                                                text={<b>{data.ad.inclusions}</b>}
                                                readLessText={` - ${t('ad:reduce')}`}
                                            />
                                        </Box>
                                    </StyledBox>
                                )}
                                <Box my={3}>
                                    <Divider />
                                </Box>
                            </Section>
                        )}

                        <Section>
                            <StyledSectionHeader>{t('ad:characteristics')}</StyledSectionHeader>
                            <Characteristics ad={data.ad as Ad} />
                        </Section>

                        <Section>
                            <StyledSectionHeader>{t('ad:neighborhood')}</StyledSectionHeader>
                            <MapGrid>
                                {data.ad.proxyLat && data.ad.proxyLng && (
                                    <GoogleMapWithMarker
                                        point={{
                                            lat: data.ad.proxyLat,
                                            lng: data.ad.proxyLng,
                                        }}
                                        approximate={data.ad.isHiddingCompleteAddress}
                                        inaccurate={data.ad.isInaccurateAddress}
                                        shouldDisplayPOIFilters
                                    />
                                )}
                            </MapGrid>
                        </Section>

                        {hasRooms && data.ad.rooms && (
                            <Section>
                                <StyledSectionHeader data-target-id="rooms-details-title">
                                    {t('ad:roomDetails')}
                                </StyledSectionHeader>
                                <StyledUnorderedList $cols={unorderedListCol}>
                                    {data.ad.rooms &&
                                        (JSON.parse(data.ad.rooms) as RoomDetailsWithStringSurface[]).map(
                                            (room, index) => (
                                                <AdDetail
                                                    key={room.type}
                                                    dataTestId={`room-${index}-details`}
                                                    leftText={t(`ad:roomTypes.${room.type}`)}
                                                    rightText={
                                                        room.surface !== ''
                                                            ? t('ad:surfaceArea', {
                                                                  surfaceArea: room.surface,
                                                              })
                                                            : t(`ad:yesNoNa.na`)
                                                    }
                                                />
                                            ),
                                        )}
                                </StyledUnorderedList>
                            </Section>
                        )}

                        <Section>
                            <StyledSectionHeader data-target-id="assets-assetsTitle">
                                {t('ad:assetsTitle')}
                            </StyledSectionHeader>
                            <Assets ad={data.ad as Ad} />
                        </Section>

                        {!isDesktop && <PartnershipsBlock partnershipBlockWidth="22rem" direction="row" />}

                        <Section>
                            <StyledSectionHeader data-target-id="consumption-energyTitle">
                                {t('ad:energyTitle')}
                            </StyledSectionHeader>
                            <Consumption ad={data.ad as Ad} />
                        </Section>

                        <Section>
                            <StyledSectionHeader>{t('ad:georisk')}</StyledSectionHeader>
                            <Typography>{t('ad:georiskBody')}</Typography>
                            <ExternalLink href="https://www.georisques.gouv.fr" target="_blank">
                                www.georisques.gouv.fr
                            </ExternalLink>
                        </Section>

                        {data.ad.type === 'sell' && data.ad.price && data.ad.livingArea && (
                            <Section>
                                <StyledSectionHeader>{t('ad:homeEvaluation.sectionTitle')}</StyledSectionHeader>
                                <PropertyEvaluationSection
                                    ad={{
                                        id: data.ad.id,
                                        price: data.ad.price,
                                        livingArea: data.ad.livingArea,
                                    }}
                                    evaluation={data.ad.homadataEvaluation ?? undefined}
                                />
                            </Section>
                        )}

                        {data.ad.price && (
                            <Section>
                                <StyledSectionHeader>{t('edit-profile:simulationTitle')}*</StyledSectionHeader>
                                <LoanSimulator
                                    initialValues={{
                                        housePrice: data.ad.price,
                                        intakeAmount: Math.round(data.ad.price * 0.15),
                                    }}
                                />
                            </Section>
                        )}
                    </Grid>

                    {isDesktop && (
                        <StickyArea lg={4} xl={4} item>
                            <StickyContainer>
                                <ContactBlock ad={data.ad} />
                                <PartnershipsBlock direction="column" />
                            </StickyContainer>
                        </StickyArea>
                    )}
                </Grid>
            </StyledContainer>
        </AnalyticsContextProvider>
    );
};

const Section: React.FC<PropsWithChildren> = ({ children }) => <Box my={3}>{children}</Box>;

AdPage.Layout = FullWidthLayout;

interface AdPageProps extends PageProps {
    ad?: AdQuery['ad'];
}

export const getServerSideProps: GetServerSideProps<AdPageProps> = async (context) => {
    const auth = getAuthCookie(context);
    const apolloClient = getApolloClient(null, auth);
    const adId = context.params?.adId as string | undefined;
    let data;
    if (adId && uuidValidate(adId)) {
        const result = await apolloClient.query<AdQuery>({
            query: Ad,
            variables: {
                id: adId,
            },
        });
        data = result.data;
    }

    const descriptionWithoutHtmlTags = removeAllHTMLTags(data?.ad?.description);

    const transactionType = data?.ad?.type ?? 'sell';
    const propertyType = data?.ad?.propertyType ?? 'other';
    const adTitleKey = `common:seo.ad.title.${transactionType}.${propertyType}`;

    return {
        props: {
            ad: data?.ad,
            ...(context.locale
                ? await serverSideTranslations(context.locale, [
                      'common',
                      'ad',
                      'edit-profile',
                      'favorites',
                      'contacts',
                      'profile',
                      'news',
                      'partnerships',
                  ])
                : {}),
            SEOProps: {
                pageTitleTranslation: {
                    key: adTitleKey,
                    params: {
                        adTitle: String(data?.ad?.title),
                    },
                },
                metaTitleTranslation: {
                    key: data?.ad?.type === 'sell' ? null : 'common:seo.ad.metaTitleRent',
                    params:
                        data?.ad?.type === 'sell'
                            ? null
                            : {
                                  rentPrice: formatPrice({
                                      price: Number(data?.ad?.rent),
                                      lang: context.locale as string,
                                  }),
                              },
                    value:
                        data?.ad?.type !== 'sell'
                            ? null
                            : formatPrice({
                                  price: Number(data.ad.price),
                                  lang: context.locale as string,
                              }),
                },
                metaDescriptionTranslation: {
                    value: descriptionWithoutHtmlTags,
                },
                imageUrl: data?.ad?.media[0]?.url ?? null,
            },
        },
    };
};

export default AdPage;
